import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { getCurrentUserID } from '../../../api/currentUserID.js';
import { processNotificationReply } from '../server/processNotificationReply.js';

/**
 * Read an already-received reply again.
 *
 * A reply is processed exactly once, when it arrives — and that one attempt can come to
 * nothing for reasons that have nothing to do with the letter: the model was unreachable, or
 * the reading step has since been improved. Without this the answer is stuck forever in a
 * mailbox the platform will never look at again, and the only way out is asking a colleague to
 * write the same mail a second time.
 *
 * Re-running is safe: every question is re-read from the database first and skipped unless it
 * is still open, so an answer already applied is never applied twice.
 */
export const reprocessNotificationReply = registerMutation({
  type: () => String,
  operationName: 'reprocessNotificationReply',
  resolve: (replyMailID: string) => {
    return async () => {
      if (!getCurrentUserID()) throw new NotLoggedInError();

      const result = await processNotificationReply(replyMailID);
      if (!result.handled)
        throw new ExpectedError('Diese Mail gehört zu keiner Rückfrage.', {
          translationKey: 'notification.not_a_reply',
        });

      return result.answered
        ? `${result.answered} Antwort(en) erkannt und verarbeitet.`
        : 'Aus dieser Mail ließ sich keine Antwort auf die offenen Fragen lesen.';
    };
  },
  params: [{ name: 'replyMailID', type: () => String }],
});
